import { Divider } from 'antd';
import Avvvatar from 'avvvatars-react';
import type { Patient } from '@/models/patient';
import {
  getInitials,
  formatDob,
  formatAgeSex,
  formatHeightWeight,
  formatReferringPhysician,
  formatStudyDate,
} from '../utils/patientFormatters';

import { PatientSidebarActions } from './PatientSidebarActions';
import styles from './PatientSidebar.module.css';

interface PatientSidebarProps {
  patient: Patient;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={styles.infoValue}>{value}</span>
    </div>
  );
}

export default function PatientSidebar({ patient }: PatientSidebarProps) {
  const initials = getInitials(patient.firstName, patient.lastName);
  const fullName = [patient.firstName, patient.lastName]
    .filter(Boolean)
    .join(' ');

  // Prefer the patient-level referring physician FK (set via patient
  // create/edit and merge overrides). Fall back to the latest study's
  // referring physician only when the patient-level field is fully blank,
  // so we don't mix first/last names from two different sources.
  const hasPatientLevelReferring =
    !!patient.referringPhysicianFirstName?.trim() ||
    !!patient.referringPhysicianLastName?.trim();
  const referringFirstName = hasPatientLevelReferring
    ? patient.referringPhysicianFirstName
    : patient.latestReferringPhysicianFirstName;
  const referringLastName = hasPatientLevelReferring
    ? patient.referringPhysicianLastName
    : patient.latestReferringPhysicianLastName;

  return (
    <div className={styles.sidebar}>
      <div className={styles.header}>
        {/* eslint-disable-next-line react/style-prop-object -- avvvatars-react uses 'style' as a variant prop, not CSS */}
        <Avvvatar value={initials} style="character" size={56} />
        <span className={styles.patientName}>{fullName}</span>
      </div>

      <Divider className={styles.divider} />

      <div className={styles.section}>
        <InfoRow label="MRN" value={patient.medicalRecordNumber || '-'} />
        <InfoRow label="DOB" value={formatDob(patient.dateOfBirth)} />
        <InfoRow
          label="Age / Sex"
          value={formatAgeSex(patient.dateOfBirth, patient.sex)}
        />
        <InfoRow
          label="Ht / Wt"
          value={formatHeightWeight(patient.height, patient.weight)}
        />
        <InfoRow
          label="Referring"
          value={formatReferringPhysician(
            referringFirstName,
            referringLastName
          )}
        />
        <InfoRow
          label="Study Date"
          value={formatStudyDate(patient.latestStudyDate)}
        />
      </div>

      <Divider className={styles.divider} />

      <div className={styles.section}>
        <InfoRow label="Address" value={patient.address?.trim() || '-'} />
        <InfoRow label="Phone" value={patient.phoneNumber?.trim() || '-'} />
        <InfoRow label="Ethnicity" value={patient.ethnicity?.trim() || '-'} />
        <InfoRow label="Hx" value={patient.cardiacHistory || '-'} />
      </div>

      <Divider className={styles.divider} />

      <div className={styles.section}>
        <span className={styles.sectionTitle}>Notes</span>
        <div className={styles.notesBox}>
          {patient.notes || 'No notes added'}
        </div>
      </div>

      <div className={styles.actionsWrapper}>
        <Divider className={styles.divider} />
        <span className={styles.sectionTitle}>Actions</span>
        <PatientSidebarActions latestStudyUuid={patient.latestStudyUuid} />
      </div>
    </div>
  );
}
